import {
  Component,
  ContentChildren,
  Directive,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';

// ---------------------------------------------------------------------------
// Column definition
// ---------------------------------------------------------------------------
export interface LightTableColumn {
  key: string;
  label: string;
  sortable?: boolean;                                      // default true
  align?: 'left' | 'center' | 'right';                   // default left
  format?: 'text' | 'number' | 'currency' | 'date' | 'boolean'; // default text
  dateFormat?: string;                                    // default 'MM/dd/yyyy'
  currencySymbol?: string;                                // default '$'
  trueLabel?: string;                                     // default 'Yes'
  falseLabel?: string;                                    // default 'No'
}

// ---------------------------------------------------------------------------
// Structural directive — marks a cell template for a named column
// Usage: <ng-template ltCell="columnKey" let-row>...</ng-template>
// ---------------------------------------------------------------------------
@Directive({selector: '[ltCell]', standalone: false})
export class LightTableCellDirective {
  @Input('ltCell') columnKey!: string;

  constructor(public templateRef: TemplateRef<any>) {
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
@Component({
  selector: 'app-light-table',
  templateUrl: './light-table.component.html',
  standalone: false,
})
export class LightTableComponent implements OnChanges {
  @Input() columns: LightTableColumn[] = [];
  @Input() rows: any[] = [];
  @Input() loading = false;
  @Input() pageSize = 25;
  @Input() pageSizeOptions: number[] = [10, 25, 50, 100];
  @Input() emptyMessage = 'No records found.';
  /** Optional function (row) => string returning Bootstrap table-* or text-* classes */
  @Input() rowClass: ((row: any) => string) | null = null;
  /** Emits {action, row} when emitAction(action, row) is called from a cell template */
  @Output() rowAction = new EventEmitter<{ action: string; row: any }>();

  emitAction(action: string, row: any): void {
    this.rowAction.emit({action, row});
  }

  @ContentChildren(LightTableCellDirective) cellTemplates!: QueryList<LightTableCellDirective>;

  sortKey = '';
  sortDir: 'asc' | 'desc' = 'asc';
  page = 1;
  _pageSize = 25;

  private _sorted: any[] = [];
  pagedRows: any[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pageSize']) {
      this._pageSize = this.pageSize;
    }
    if (changes['rows'] || changes['pageSize']) {
      this.page = 1;
      this.applySort();
    }
  }

  // ---- sorting ----

  onHeaderClick(col: LightTableColumn): void {
    if (col.sortable === false) {
      return;
    }
    if (this.sortKey === col.key) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = col.key;
      this.sortDir = 'asc';
    }
    this.page = 1;
    this.applySort();
  }

  private applySort(): void {
    const src = this.rows ?? [];
    if (!this.sortKey) {
      this._sorted = [...src];
    } else {
      const dir = this.sortDir === 'asc' ? 1 : -1;
      this._sorted = [...src].sort((a, b) => {
        const av = this.getVal(a, this.sortKey);
        const bv = this.getVal(b, this.sortKey);
        if (av == null && bv == null) {
          return 0;
        }
        if (av == null) {
          return 1;
        }
        if (bv == null) {
          return -1;
        }
        if (typeof av === 'number' && typeof bv === 'number') {
          return (av - bv) * dir;
        }
        return String(av).localeCompare(String(bv)) * dir;
      });
    }
    this.buildPage();
  }

  private buildPage(): void {
    const start = (this.page - 1) * this._pageSize;
    this.pagedRows = this._sorted.slice(start, start + this._pageSize);
  }

  // ---- paging ----

  get pageEnd(): number {
    return Math.min(this.page * this._pageSize, this._sorted?.length ?? 0);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil((this._sorted?.length ?? 0) / this._pageSize));
  }

  get pageNumbers(): number[] {
    const total = this.totalPages;
    const cur = this.page;
    const delta = 2;
    const range: number[] = [];
    for (let i = Math.max(1, cur - delta); i <= Math.min(total, cur + delta); i++) {
      range.push(i);
    }
    return range;
  }

  onPageChange(p: number): void {
    if (p < 1 || p > this.totalPages) {
      return;
    }
    this.page = p;
    this.buildPage();
  }

  onPageSizeChange(val: string): void {
    this._pageSize = +val;
    this.page = 1;
    this.buildPage();
  }

  // ---- cell rendering ----

  getCellTemplate(key: string): TemplateRef<any> | null {
    if (!this.cellTemplates) {
      return null;
    }
    const found = this.cellTemplates.find(t => t.columnKey === key);
    return found ? found.templateRef : null;
  }

  formatValue(row: any, col: LightTableColumn): string {
    const val = this.getVal(row, col.key);
    if (val == null || val === '') {
      return '—';
    }
    switch (col.format) {
      case 'currency': {
        const sym = col.currencySymbol ?? '$';
        const n = parseFloat(val);
        return isNaN(n) ? '—' : sym + n.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
      }
      case 'number': {
        const n = parseFloat(val);
        return isNaN(n) ? '—' : n.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
      }
      case 'date': {
        const d = new Date(val);
        if (isNaN(d.getTime())) {
          return val;
        }
        const fmt = col.dateFormat ?? 'MM/dd/yyyy';
        if (fmt === 'MM/dd/yyyy') {
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          return `${mm}/${dd}/${d.getFullYear()}`;
        }
        return d.toLocaleDateString();
      }
      case 'boolean':
        return val ? (col.trueLabel ?? 'Yes') : (col.falseLabel ?? 'No');
      default:
        return String(val);
    }
  }

  getRowClass(row: any): string {
    return this.rowClass ? this.rowClass(row) : '';
  }

  sortIcon(key: string): string {
    if (this.sortKey !== key) {
      return 'bi-arrow-down-up text-muted';
    }
    return this.sortDir === 'asc' ? 'bi-sort-up' : 'bi-sort-down';
  }

  alignClass(col: LightTableColumn): string {
    switch (col.align) {
      case 'center':
        return 'text-center';
      case 'right':
        return 'text-end';
      default:
        return '';
    }
  }

  private getVal(row: any, key: string): any {
    if (!row || !key) {
      return null;
    }
    // support dot-notation: 'address.city'
    return key.split('.').reduce((obj, k) => (obj != null ? obj[k] : null), row);
  }
}
